// ---------------------------------------------------------------------------
// What a receipt and a preparation ticket look like, on this side of the wire.
//
// These mirror `backend/src/config/designDefaults.ts`. Two shapes, deliberately
// separate, with nothing shared between them: the receipt carries money and the
// preparation ticket carries none. There is no base type they both extend,
// because the moment there is one, somebody adds a "showTotal" to it and a
// price appears on a cutting instruction.
//
// The backend is the authority. It parses every config with a `.strict()`
// schema and refuses a field it does not recognise, so these types describe
// what will be accepted rather than deciding it. A field added here and not
// there gets a 400, which is the right way round.
// ---------------------------------------------------------------------------

export type DesignSize = 'SMALL' | 'MEDIUM' | 'LARGE';
export type DesignAlignment = 'LEFT' | 'CENTER' | 'RIGHT';
export type DesignLanguage = 'EN' | 'AR' | 'BILINGUAL';
export type DesignDivider = 'SOLID' | 'DASHED' | 'DOTTED' | 'NONE';
export type PaperWidth = 58 | 80;

export const RECEIPT_TEMPLATES = ['CLASSIC', 'MODERN', 'COMPACT', 'BILINGUAL'] as const;
export type ReceiptTemplate = (typeof RECEIPT_TEMPLATES)[number];

export const KITCHEN_TEMPLATES = [
  'CLASSIC',
  'LARGE_PREPARATION',
  'COMPACT',
  'BILINGUAL',
] as const;
export type KitchenTemplate = (typeof KITCHEN_TEMPLATES)[number];

// ---------------------------------------------------------------------------

export interface ReceiptConfig {
  template: ReceiptTemplate;
  widthMm: PaperWidth;
  language: DesignLanguage;
  fontSize: DesignSize;
  density: 'COMFORTABLE' | 'COMPACT';
  dividerStyle: DesignDivider;

  showLogo: boolean;
  logoSize: DesignSize;
  logoAlignment: DesignAlignment;
  showBusinessNameEn: boolean;
  showBusinessNameAr: boolean;
  showAddress: boolean;
  showPhone: boolean;
  headerText: string;
  footerText: string;
  thankYouText: string;
  footerAlignment: DesignAlignment;

  showReceiptNumber: boolean;
  showOrderNumber: boolean;
  showSaleNumber: boolean;
  showCashierName: boolean;
  showCustomerName: boolean;
  /// The online menu order block (number, name, phone, address) on a
  /// receipt for an order that came from the menu. Optional so every saved
  /// design keeps working: absent means shown.
  showOnlineOrderDetails?: boolean;
  showDateTime: boolean;

  showSku: boolean;
  showItemBarcode: boolean;
  showWeight: boolean;
  showQuantity: boolean;
  showUnitPrice: boolean;
  showItemNotes: boolean;

  showSubtotal: boolean;
  showDeliveryFee: boolean;
  showDiscount: boolean;
  showTotalUsd: boolean;
  showTotalLbp: boolean;
  showExchangeRate: boolean;
  showPaymentMethods: boolean;
  showAmountReceived: boolean;
  showChange: boolean;

  showQrCode: boolean;
  showReceiptBarcode: boolean;

  copies: number;
  autoPrint: boolean;
}

/// No money field of any kind. Not hidden behind a flag - absent.
export interface KitchenConfig {
  template: KitchenTemplate;
  widthMm: PaperWidth;
  language: DesignLanguage;
  fontSize: DesignSize;
  productNameSize: DesignSize;
  boldProductName: boolean;
  boldQuantity: boolean;
  dividerStyle: DesignDivider;

  showLogo: boolean;
  logoSize: DesignSize;
  logoAlignment: DesignAlignment;

  showOrderNumber: boolean;
  showSaleNumber: boolean;
  showCustomerName: boolean;
  showCustomerPhone: boolean;
  showDeliveryAddress: boolean;
  showRequestedTime: boolean;
  showCashierName: boolean;
  showOrderType: boolean;
  showDateTime: boolean;

  showWeightOrQuantity: boolean;
  showItemNotes: boolean;
  showCuttingInstructions: boolean;
  showOrderNotes: boolean;
  showCategory: boolean;
  showPreparationStatus: boolean;

  showDeliveryHeading: boolean;
  showUrgentHeading: boolean;
  handwritingSpaceLines: number;

  copies: number;
  autoPrint: boolean;
}

// ---------------------------------------------------------------------------

export interface DesignVersion<TConfig> {
  id: string;
  version: number;
  config: TConfig;
  createdAt: string;
  createdBy: { id: string; fullName: string } | null;
}

export interface Design<TConfig> {
  id: string;
  name: string;
  template: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: { id: string; fullName: string } | null;
  /// Newest first. The one a new sale would be stamped with is `versions[0]`
  /// of whichever design is active.
  versions: Array<DesignVersion<TConfig>>;
}

export type ShiftReportTemplate = 'CLASSIC' | 'COMPACT';

/// The shift closing report's layout. The FIGURES are never configurable -
/// they come frozen from the shift's snapshot - only which sections appear and
/// how the paper behaves.
export interface ShiftReportConfig {
  template: ShiftReportTemplate;
  widthMm: 58 | 80;
  language: 'EN' | 'AR' | 'BILINGUAL';
  fontSize: 'SMALL' | 'MEDIUM' | 'LARGE';
  dividerStyle: 'SOLID' | 'DASHED' | 'DOTTED' | 'NONE';

  showBusinessName: boolean;
  showReportTitle: boolean;
  showShiftNumber: boolean;
  showCashier: boolean;
  showOpenedAt: boolean;
  showClosedAt: boolean;
  showDuration: boolean;

  showGrossSales: boolean;
  showDiscounts: boolean;
  showNetSales: boolean;
  showSaleCount: boolean;
  showAverageSale: boolean;

  showPayments: boolean;
  showTransactionCounts: boolean;

  showOpeningCash: boolean;
  showExpectedCash: boolean;
  showCountedCash: boolean;
  showDifference: boolean;

  showVoids: boolean;
  showExpenses: boolean;
  showClosingNote: boolean;

  showClosedBy: boolean;
  showPrintedAt: boolean;
  footerText: string;

  autoPrint: boolean;
  copies: number;
}

export type ShiftReportDesign = Design<ShiftReportConfig>;

export type ReceiptDesign = Design<ReceiptConfig>;
export type KitchenDesign = Design<KitchenConfig>;

export interface DesignListResponse<TConfig> {
  designs: Array<Design<TConfig>>;
  defaults: TConfig;
  templates: string[];
  paperWidths: number[];
}

/// What the till prints with right now.
export interface PrintConfigResponse {
  receipt: ReceiptConfig;
  shiftReport: ShiftReportConfig;
  shiftReportVersionId: string | null;
  receiptVersionId: string | null;
  kitchen: KitchenConfig;
  kitchenVersionId: string | null;
}

// ---------------------------------------------------------------------------
// The layouts to fall back to
// ---------------------------------------------------------------------------

/// What to print with when the server has not answered yet, or cannot.
///
/// These are the same defaults the backend serves for a shop with no design,
/// which is the layout the system had before the designers existed. A till
/// whose layout request fails must still put paper in a customer's hand: a
/// spinner where the receipt should be is a shop that has stopped trading.
///
/// The one thing that never falls back is AUTOMATIC printing. The dialog waits
/// for the real layout, so the system never prints one thing and shows another.
export const FALLBACK_RECEIPT_CONFIG: ReceiptConfig = {
  template: 'CLASSIC',
  widthMm: 80,
  language: 'BILINGUAL',
  fontSize: 'MEDIUM',
  density: 'COMFORTABLE',
  dividerStyle: 'DASHED',

  showLogo: true,
  logoSize: 'MEDIUM',
  logoAlignment: 'CENTER',
  showBusinessNameEn: true,
  showBusinessNameAr: true,
  showAddress: true,
  showPhone: true,
  headerText: '',
  footerText: '',
  thankYouText: '',
  footerAlignment: 'CENTER',

  showReceiptNumber: true,
  showOrderNumber: true,
  showSaleNumber: false,
  showCashierName: true,
  showCustomerName: true,
  showDateTime: true,

  showSku: false,
  showItemBarcode: false,
  showWeight: true,
  showQuantity: true,
  showUnitPrice: true,
  showItemNotes: true,

  showSubtotal: true,
  showDeliveryFee: true,
  showDiscount: true,
  showTotalUsd: true,
  showTotalLbp: true,
  showExchangeRate: true,
  showPaymentMethods: true,
  showAmountReceived: true,
  showChange: true,

  showQrCode: false,
  showReceiptBarcode: true,

  copies: 1,
  autoPrint: false,
};

/// No money field, here either.
export const FALLBACK_KITCHEN_CONFIG: KitchenConfig = {
  template: 'CLASSIC',
  widthMm: 80,
  language: 'BILINGUAL',
  fontSize: 'MEDIUM',
  productNameSize: 'LARGE',
  boldProductName: true,
  boldQuantity: true,
  dividerStyle: 'DASHED',

  showLogo: true,
  logoSize: 'SMALL',
  logoAlignment: 'CENTER',

  showOrderNumber: true,
  showSaleNumber: true,
  showCustomerName: true,
  showCustomerPhone: false,
  showDeliveryAddress: true,
  showRequestedTime: true,
  showCashierName: true,
  showOrderType: true,
  showDateTime: true,

  showWeightOrQuantity: true,
  showItemNotes: true,
  showCuttingInstructions: true,
  showOrderNotes: true,
  showCategory: true,
  showPreparationStatus: false,

  showDeliveryHeading: true,
  showUrgentHeading: false,
  handwritingSpaceLines: 0,

  copies: 1,
  autoPrint: true,
};
